/**
 * CreatorRecorder — TikTok-style camera with the works:
 *  • Front/back camera flip
 *  • Pause / resume MediaRecorder
 *  • Multi-clip recording (stitch via canvas.captureStream)
 *  • Timer / hands-free countdown (3s / 10s)
 *  • Color filters (incl. Cinematic) + Beauty soften with intensity slider
 *  • AR effects: face stickers (sunglasses / cat ears / heart eyes / crown)
 *    via MediaPipe FaceLandmarker
 *  • AI background blur / replace (custom URL or gradient) via MediaPipe
 *    ImageSegmenter (selfie segmentation)
 *  • Adjustable playback speed (0.5x / 1x / 2x) on preview
 *
 * The pipeline:
 *   <hidden video> ←─ camera stream
 *        ↓ (rAF)
 *   ┌─ FaceLandmarker (when AR on)
 *   ├─ ImageSegmenter  (when bgFx on)
 *   └─ Canvas composite (filter, beauty, segmentation, AR overlays)
 *        ↓
 *   <visible canvas> + canvas.captureStream() → MediaRecorder
 *
 * NOTE: MediaPipe runs entirely in the browser via WASM. Models are
 * fetched from the public Google CDN. A graceful fallback ensures the
 * recorder still works (without AR / bg-blur) when the model fails to
 * load — e.g. on offline or older browsers.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Camera, SwitchCamera, Play, Pause as PauseIcon,
} from "lucide-react";
import FxPanel from "./recorder/FxPanel";
import ClipReview from "./recorder/ClipReview";

const COLOR_FILTERS = [
  { id: "none",      label: "None",      css: "none" },
  { id: "vibrant",   label: "Vibrant",   css: "saturate(1.6) contrast(1.1)" },
  { id: "cinematic", label: "Cinematic", css: "contrast(1.15) saturate(1.05) brightness(0.95) sepia(0.08)" },
  { id: "mono",      label: "Mono",      css: "grayscale(1) contrast(1.1)" },
  { id: "warm",      label: "Warm",      css: "saturate(1.2) hue-rotate(-10deg) brightness(1.05)" },
  { id: "cool",      label: "Cool",      css: "saturate(1.1) hue-rotate(15deg) brightness(0.95)" },
  { id: "neon",      label: "Neon",      css: "saturate(2) contrast(1.2) hue-rotate(20deg)" },
];

const AR_STICKERS = [
  { id: "off",       label: "Off",       emoji: null },
  { id: "shades",    label: "Shades",    emoji: "🕶️" },
  { id: "ears",      label: "Cat ears",  emoji: "🐱" },
  { id: "hearts",    label: "Hearts",    emoji: "😍" },
  { id: "crown",     label: "Crown",     emoji: "👑" },
  { id: "party",     label: "Party",     emoji: "🥳" },
];

const BG_FX = [
  { id: "off",        label: "Off" },
  { id: "blur",       label: "Blur" },
  { id: "neon",       label: "Neon",   gradient: "linear-gradient(135deg,#FF007F,#D4FF00,#00F0FF)" },
  { id: "sunset",     label: "Sunset", gradient: "linear-gradient(135deg,#FF512F,#F09819)" },
  { id: "ocean",      label: "Ocean",  gradient: "linear-gradient(135deg,#2E3192,#1BFFFF)" },
];

const TIMERS = [0, 3, 10];
const SPEEDS  = [0.5, 1, 2];

const FACELM_MODEL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const SEG_MODEL    = "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite";
const WASM_BASE    = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

export default function CreatorRecorder({ onPosted, storeReady }) {
  const videoRef    = useRef(null);     // hidden source video
  const canvasRef   = useRef(null);     // visible composited canvas
  const previewRef  = useRef(null);     // playback after recording
  const offscreenRef = useRef(null);    // for last-frame thumbnail
  const recRef      = useRef(null);
  const recChunksRef = useRef([]);
  const rafRef      = useRef(0);

  // MediaPipe runtimes (lazy)
  const faceLmRef   = useRef(null);
  const segRef      = useRef(null);
  const arLoadingRef = useRef(false);
  const segLoadingRef = useRef(false);

  const [stream, setStream]     = useState(null);
  const [front, setFront]       = useState(true);
  const [micOn, setMicOn]       = useState(true);

  // Recording state
  const [recState, setRecState] = useState("idle");  // idle | countdown | recording | paused | done
  const [countdown, setCountdown] = useState(0);
  const [clips, setClips]       = useState([]);      // [{url, blob, ms}]
  const [activeClipMs, setActiveClipMs] = useState(0);
  const clipStartRef = useRef(0);
  const clipPausedAccRef = useRef(0);
  const clipPauseStartRef = useRef(0);

  // FX
  const [filter, setFilter]      = useState("none");
  const [beauty, setBeauty]      = useState(0);      // 0-3
  const [arSticker, setArSticker] = useState("off");
  const [bgFx, setBgFx]          = useState("off");
  const [bgImage, setBgImage]    = useState(null);   // optional custom bg URL

  const [timer, setTimer]   = useState(0);
  const [speed, setSpeed]   = useState(1);

  const filterCss = useMemo(() => {
    const c = COLOR_FILTERS.find(f => f.id === filter)?.css || "none";
    if (!beauty) return c;
    const b = ` blur(${0.4 + beauty * 0.25}px) brightness(${1 + beauty * 0.02}) saturate(${1 + beauty * 0.04})`;
    return c === "none" ? b.trim() : c + b;
  }, [filter, beauty]);

  // ─────────────────────────────────────────────────────────────
  // MediaPipe loaders (deferred until needed)
  // ─────────────────────────────────────────────────────────────
  const loadFaceLm = useCallback(async () => {
    if (faceLmRef.current || arLoadingRef.current) return faceLmRef.current;
    arLoadingRef.current = true;
    try {
      const mp = await import("@mediapipe/tasks-vision");
      const fileset = await mp.FilesetResolver.forVisionTasks(WASM_BASE);
      faceLmRef.current = await mp.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: FACELM_MODEL, delegate: "GPU" },
        runningMode: "VIDEO",
        numFaces: 1,
      });
    } catch (e) {
      console.warn("FaceLandmarker load failed; AR effects disabled.", e);
      faceLmRef.current = null;
    } finally { arLoadingRef.current = false; }
    return faceLmRef.current;
  }, []);

  const loadSeg = useCallback(async () => {
    if (segRef.current || segLoadingRef.current) return segRef.current;
    segLoadingRef.current = true;
    try {
      const mp = await import("@mediapipe/tasks-vision");
      const fileset = await mp.FilesetResolver.forVisionTasks(WASM_BASE);
      segRef.current = await mp.ImageSegmenter.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: SEG_MODEL, delegate: "GPU" },
        runningMode: "VIDEO",
        outputCategoryMask: true,
      });
    } catch (e) {
      console.warn("ImageSegmenter load failed; bg blur disabled.", e);
      segRef.current = null;
    } finally { segLoadingRef.current = false; }
    return segRef.current;
  }, []);

  useEffect(() => {
    if (arSticker !== "off") loadFaceLm();
  }, [arSticker, loadFaceLm]);
  useEffect(() => {
    if (bgFx !== "off") loadSeg();
  }, [bgFx, loadSeg]);

  // ─────────────────────────────────────────────────────────────
  // Camera lifecycle
  // ─────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: front ? "user" : "environment", width: { ideal: 720 } },
        audio: micOn,
      });
      setStream(s);
      const v = videoRef.current;
      if (v) { v.srcObject = s; v.muted = true; await v.play().catch(()=>{}); }
    } catch {
      alert("Camera permission required to record.");
    }
  }, [front, micOn]);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
  }, [stream]);

  useEffect(() => () => {
    stopCamera();
    clips.forEach(c => URL.revokeObjectURL(c.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-attach stream to <video> on mount of the element
  const setVideoEl = (el) => {
    videoRef.current = el;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      el.muted = true; el.play().catch(()=>{});
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Composite render loop (runs whenever stream is active)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!stream) return;
    const cv = canvasRef.current;
    const v  = videoRef.current;
    if (!cv || !v) return;
    cv.width  = 720;
    cv.height = 1280;
    const ctx = cv.getContext("2d");

    const bgImg = bgImage ? Object.assign(new Image(), { src: bgImage, crossOrigin: "anonymous" }) : null;

    const draw = async () => {
      rafRef.current = requestAnimationFrame(draw);
      if (!v.videoWidth) return;

      // Draw video first, scaled "cover"
      const vw = v.videoWidth, vh = v.videoHeight;
      const targetAR = cv.width / cv.height;
      const srcAR = vw / vh;
      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (srcAR > targetAR) { sw = vh * targetAR; sx = (vw - sw) / 2; }
      else                  { sh = vw / targetAR; sy = (vh - sh) / 2; }

      ctx.save();
      // Mirror selfie for natural feel
      if (front) { ctx.translate(cv.width, 0); ctx.scale(-1, 1); }
      ctx.filter = filterCss;
      ctx.drawImage(v, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
      ctx.restore();

      // ── Background segmentation
      if (bgFx !== "off" && segRef.current) {
        try {
          const ts = performance.now();
          const result = segRef.current.segmentForVideo(v, ts);
          const mask = result.categoryMask;
          if (mask) {
            // Person mask: pixels==0 are person (selfie segmenter); >0 = bg
            const maskData = mask.getAsUint8Array();
            const mw = mask.width, mh = mask.height;
            const tmp = document.createElement("canvas");
            tmp.width = mw; tmp.height = mh;
            const tctx = tmp.getContext("2d");
            const id = tctx.createImageData(mw, mh);
            for (let i = 0; i < maskData.length; i++) {
              const a = maskData[i] === 0 ? 255 : 0;   // alpha for person
              id.data[i*4]   = 255;
              id.data[i*4+1] = 255;
              id.data[i*4+2] = 255;
              id.data[i*4+3] = a;
            }
            tctx.putImageData(id, 0, 0);

            // Mask scale-factors. The mask is at the full input video resolution,
            // but we drew the video using a "cover" source crop (sx,sy,sw,sh).
            // To keep the mask aligned with the visible image we MUST sample the
            // same source rectangle from the mask.
            const mxRatio = mw / vw, myRatio = mh / vh;
            const msx = sx * mxRatio, msy = sy * myRatio;
            const msw = sw * mxRatio, msh = sh * myRatio;

            // Build the new background canvas
            const bgC = document.createElement("canvas");
            bgC.width = cv.width; bgC.height = cv.height;
            const bgCtx = bgC.getContext("2d");
            const bgFxDef = BG_FX.find(b => b.id === bgFx);
            if (bgFx === "blur") {
              // Strong blur of original video as the bg (use same crop + mirror as person)
              bgCtx.save();
              if (front) { bgCtx.translate(cv.width, 0); bgCtx.scale(-1, 1); }
              bgCtx.filter = "blur(22px) brightness(0.85)";
              bgCtx.drawImage(v, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
              bgCtx.restore();
            } else if (bgImg && bgImg.complete && bgImg.naturalWidth) {
              const ar = bgImg.naturalWidth / bgImg.naturalHeight;
              let dW = cv.width, dH = cv.width / ar;
              if (dH < cv.height) { dH = cv.height; dW = cv.height * ar; }
              bgCtx.drawImage(bgImg, (cv.width - dW)/2, (cv.height - dH)/2, dW, dH);
            } else if (bgFxDef?.gradient) {
              const grd = bgCtx.createLinearGradient(0, 0, cv.width, cv.height);
              const colors = bgFxDef.gradient.match(/#[0-9A-Fa-f]{6}/g) || ["#000", "#222"];
              colors.forEach((c, i) => grd.addColorStop(i / Math.max(1, colors.length - 1), c));
              bgCtx.fillStyle = grd;
              bgCtx.fillRect(0, 0, cv.width, cv.height);
            }

            // Composite: keep person pixels from existing canvas, replace bg with bgC
            // 1) destination-in with the cropped + mirrored mask → erases bg (non-person)
            //    pixels from cv.
            // 2) destination-over with bgC → draws the new background behind.
            ctx.save();
            ctx.globalCompositeOperation = "destination-in";
            if (front) { ctx.translate(cv.width, 0); ctx.scale(-1, 1); }
            ctx.drawImage(tmp, msx, msy, msw, msh, 0, 0, cv.width, cv.height);
            ctx.restore();
            ctx.save();
            ctx.globalCompositeOperation = "destination-over";
            ctx.drawImage(bgC, 0, 0);
            ctx.restore();
          }
        } catch (e) { /* swallow per-frame errors */ }
      }

      // ── AR sticker overlay
      if (arSticker !== "off" && faceLmRef.current) {
        try {
          const ts = performance.now();
          const r = faceLmRef.current.detectForVideo(v, ts);
          const lm = r.faceLandmarks?.[0];
          if (lm) {
            // Landmarks are normalized to the FULL input video frame.
            // Remap them through the same source-crop rectangle used to
            // draw the video so they line up on the canvas. Then mirror.
            const toCv = (p) => {
              const px = p.x * vw, py = p.y * vh;
              let cnx = (px - sx) / sw;
              let cny = (py - sy) / sh;
              if (front) cnx = 1 - cnx;
              return { x: cnx * cv.width, y: cny * cv.height };
            };
            const lEye = toCv(lm[33]);
            const rEye = toCv(lm[263]);
            const fore = toCv(lm[10]);
            const cx   = (lEye.x + rEye.x) / 2;
            const cy   = (lEye.y + rEye.y) / 2;
            const w    = Math.hypot(rEye.x - lEye.x, rEye.y - lEye.y) * 3.0;
            // Angle MUST be computed from the un-mirrored landmarks and
            // negated when the canvas is mirrored — otherwise the selfie
            // flip turns rEye.x − lEye.x negative, producing a ~180°
            // angle that renders every emoji upside-down (TikTok/IG do
            // this same trick under the hood).
            const lOrig = lm[33], rOrig = lm[263];
            const angOrig = Math.atan2(rOrig.y - lOrig.y, rOrig.x - lOrig.x);
            const ang = front ? -angOrig : angOrig;

            ctx.save();
            const stick = AR_STICKERS.find(s => s.id === arSticker);
            if (stick?.emoji) {
              // Anchor offset: ears/crown sit above the forehead, others on eyes.
              const anchorX = (arSticker === "ears" || arSticker === "crown") ? fore.x : cx;
              const anchorY = (arSticker === "ears" || arSticker === "crown")
                ? fore.y - Math.abs(cy - fore.y) * 0.7
                : cy;
              ctx.translate(anchorX, anchorY);
              ctx.rotate(ang);
              ctx.font = `${Math.round(w)}px serif`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(stick.emoji, 0, 0);
            }
            ctx.restore();
          }
        } catch {}
      }
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [stream, front, filterCss, arSticker, bgFx, bgImage]);

  // ─────────────────────────────────────────────────────────────
  // Recording controls
  // ─────────────────────────────────────────────────────────────
  const startRecording = useCallback(() => {
    if (!stream || !canvasRef.current) return;
    // Build a stream from the canvas + the original audio track
    const composed = canvasRef.current.captureStream(30);
    if (micOn) {
      stream.getAudioTracks().forEach(t => composed.addTrack(t));
    }
    let mr;
    try { mr = new MediaRecorder(composed, { mimeType: "video/webm;codecs=vp9,opus" }); }
    catch {
      try { mr = new MediaRecorder(composed, { mimeType: "video/webm" }); }
      catch { alert("Recording not supported in this browser."); return; }
    }
    recChunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data?.size) recChunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(recChunksRef.current, { type: "video/webm" });
      const ms = Date.now() - clipStartRef.current - clipPausedAccRef.current;
      const url = URL.createObjectURL(blob);
      setClips(c => [...c, { blob, url, ms }]);
      setRecState("done");
      setActiveClipMs(0);
    };
    mr.start(200);
    recRef.current = mr;
    clipStartRef.current = Date.now();
    clipPausedAccRef.current = 0;
    setRecState("recording");
  }, [stream, micOn]);

  const beginCountdown = () => {
    if (timer === 0) { startRecording(); return; }
    setRecState("countdown");
    setCountdown(timer);
  };
  // Countdown driver
  useEffect(() => {
    if (recState !== "countdown") return;
    if (countdown <= 0) { startRecording(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [recState, countdown, startRecording]);

  // Active clip timer
  useEffect(() => {
    if (recState !== "recording") return;
    const id = setInterval(() => {
      setActiveClipMs(Date.now() - clipStartRef.current - clipPausedAccRef.current);
    }, 100);
    return () => clearInterval(id);
  }, [recState]);

  const pauseRecording  = () => {
    if (!recRef.current || recRef.current.state !== "recording") return;
    recRef.current.pause();
    clipPauseStartRef.current = Date.now();
    setRecState("paused");
  };
  const resumeRecording = () => {
    if (!recRef.current || recRef.current.state !== "paused") return;
    recRef.current.resume();
    clipPausedAccRef.current += Date.now() - clipPauseStartRef.current;
    setRecState("recording");
  };
  const finishClip = () => {
    if (!recRef.current) return;
    recRef.current.stop();
  };
  const recordAnother = () => {
    setRecState("idle");
  };
  const dropAllClips = () => {
    clips.forEach(c => URL.revokeObjectURL(c.url));
    setClips([]); setRecState("idle");
  };

  const totalMs = clips.reduce((s, c) => s + c.ms, 0) + activeClipMs;
  const fmt = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  };

  // ─────────────────────────────────────────────────────────────
  // Post handler — currently posts the LAST clip (multi-clip stitching
  // is left to a future media server; the mock backend stores a thumbnail).
  // ─────────────────────────────────────────────────────────────
  const handlePost = async () => {
    if (!clips.length || !storeReady) return;
    // Capture thumbnail from preview <video> first frame
    const last = clips[clips.length - 1];
    const v = document.createElement("video");
    v.src = last.url; v.muted = true;
    await new Promise(r => v.addEventListener("loadeddata", () => { v.currentTime = 0.1; r(); }, { once: true }));
    await new Promise(r => v.addEventListener("seeked", r, { once: true }));
    const tmp = document.createElement("canvas");
    tmp.width = 360; tmp.height = 640;
    const tctx = tmp.getContext("2d");
    tctx.drawImage(v, 0, 0, 360, 640);
    const thumbnail = tmp.toDataURL("image/jpeg", 0.7);
    onPosted({
      thumbnail,
      fx: { filter, beauty, arSticker, bgFx, clip_count: clips.length, total_ms: totalMs, speed },
    });
  };

  // ─────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────
  const showPreview = recState === "done" && clips.length > 0;
  const lastClip = clips[clips.length - 1];

  return (
    <div className="card overflow-hidden" data-testid="creator-recorder">
      {/* Hidden source video — never shown. Canvas is the visible surface. */}
      <video ref={setVideoEl} playsInline autoPlay muted className="hidden"/>

      {/* Stage — sized responsively. On desktop the available width often
          dominates, so we use a flexible max-width instead of a height cap.
          On phones, max-h ensures the controls remain reachable. */}
      <div className="relative bg-black overflow-hidden mx-auto"
        style={{ aspectRatio: "9/16", width: "100%", maxWidth: "min(540px, 92vw)" }}>
        {!showPreview && (
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" data-testid="creator-canvas"/>
        )}
        {showPreview && (
          <video
            ref={el => {
              previewRef.current = el;
              if (el) el.playbackRate = speed;
            }}
            src={lastClip.url}
            controls loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="creator-preview-video"
          />
        )}

        {/* Live REC chip */}
        {recState === "recording" && (
          <div className="absolute top-3 left-3 bg-[#FF007F] text-white text-xs font-bold uppercase px-2 py-1 rounded animate-pulse" data-testid="rec-live-chip">
            ● REC {fmt(activeClipMs)}
          </div>
        )}
        {recState === "paused" && (
          <div className="absolute top-3 left-3 bg-[#FFA500] text-black text-xs font-bold uppercase px-2 py-1 rounded">
            ⏸ PAUSED · {fmt(activeClipMs)}
          </div>
        )}
        {clips.length > 0 && recState !== "done" && (
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded font-mono-display" data-testid="rec-clip-pill">
            {clips.length} clip{clips.length !== 1 ? "s" : ""} · {fmt(totalMs)}
          </div>
        )}
        {recState === "countdown" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="rec-countdown">
            <span className="font-display font-black text-white" style={{ fontSize: "10rem", lineHeight: 1 }}>{countdown}</span>
          </div>
        )}

        {/* Speed pill on preview */}
        {showPreview && (
          <div className="absolute bottom-16 right-3 flex flex-col gap-1" data-testid="creator-speed-bar">
            {SPEEDS.map(s => (
              <button key={s} onClick={() => { setSpeed(s); if (previewRef.current) previewRef.current.playbackRate = s; }}
                data-testid={`speed-${s}`}
                className={`w-9 h-9 rounded-full text-[10px] font-bold flex items-center justify-center transition ${
                  speed === s ? "bg-[#D4FF00] text-black" : "bg-black/60 text-white hover:bg-black/80"
                }`}>{s}x</button>
            ))}
          </div>
        )}

        {/* Floating mobile-camera-style shutter, pause and end buttons.
            Always visible inside the stage when the camera is live. */}
        {stream && !showPreview && recState !== "countdown" && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-4 flex items-center gap-5 z-20" data-testid="floating-shutter-bar">
            {/* LEFT slot — Pause / Resume */}
            <div className="w-12 h-12 flex items-center justify-center">
              {recState === "recording" && (
                <button onClick={pauseRecording} aria-label="Pause"
                  data-testid="rec-pause-fab"
                  className="w-12 h-12 rounded-full bg-black/70 text-white border border-white/40 flex items-center justify-center backdrop-blur active:scale-90 transition">
                  <PauseIcon size={20}/>
                </button>
              )}
              {recState === "paused" && (
                <button onClick={resumeRecording} aria-label="Resume"
                  data-testid="rec-resume-fab"
                  className="w-12 h-12 rounded-full bg-black/70 text-white border border-white/40 flex items-center justify-center backdrop-blur active:scale-90 transition">
                  <Play size={20} className="fill-white"/>
                </button>
              )}
            </div>

            {/* CENTER — main shutter */}
            {recState === "idle" && (
              <button onClick={beginCountdown} aria-label="Record"
                data-testid="rec-shutter-fab"
                className="w-20 h-20 rounded-full bg-white/20 border-4 border-white flex items-center justify-center backdrop-blur active:scale-95 transition shadow-2xl">
                <span className="block w-14 h-14 rounded-full bg-[#FF007F]"/>
              </button>
            )}
            {(recState === "recording" || recState === "paused") && (
              <button onClick={finishClip} aria-label="End clip"
                data-testid="rec-stop-fab"
                className="w-20 h-20 rounded-full bg-white/20 border-4 border-white flex items-center justify-center backdrop-blur active:scale-95 transition shadow-2xl">
                <span className="block w-9 h-9 rounded-md bg-[#FF007F]"/>
              </button>
            )}

            {/* RIGHT slot — Flip camera */}
            <div className="w-12 h-12 flex items-center justify-center">
              {recState === "idle" && (
                <button onClick={() => { setFront(!front); stopCamera(); setTimeout(startCamera, 120); }}
                  aria-label="Flip camera"
                  data-testid="rec-flip-fab"
                  className="w-12 h-12 rounded-full bg-black/70 text-white border border-white/40 flex items-center justify-center backdrop-blur active:scale-90 transition">
                  <SwitchCamera size={18}/>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      {!stream && !showPreview && (
        <div className="p-4 flex justify-center bg-[#261534]">
          <button onClick={startCamera} data-testid="rec-start-cam" className="btn-primary">
            <Camera size={14} className="inline mr-1.5"/> Start camera
          </button>
        </div>
      )}

      {stream && !showPreview && (
        <div className="p-3 sm:p-4 bg-[#261534]">
          <FxPanel
            COLOR_FILTERS={COLOR_FILTERS} filter={filter} setFilter={setFilter}
            beauty={beauty} setBeauty={setBeauty}
            AR_STICKERS={AR_STICKERS} arSticker={arSticker} setArSticker={setArSticker}
            BG_FX={BG_FX} bgFx={bgFx} setBgFx={setBgFx}
            setBgImage={setBgImage} loadSeg={loadSeg}
            TIMERS={TIMERS} timer={timer} setTimer={setTimer}
            micOn={micOn} setMicOn={setMicOn}
            clips={clips} recState={recState} onReview={() => setRecState("done")}
          />
        </div>
      )}

      {/* Preview after clips done */}
      {showPreview && (
        <ClipReview
          clips={clips} totalMs={totalMs} storeReady={storeReady}
          onAddClip={recordAnother} onRedo={dropAllClips} onPost={handlePost}
          fmt={fmt}
        />
      )}
    </div>
  );
}
